import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { subject } from '@casl/ability';
import { useAbility } from '@casl/react';
import { CircularProgress } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { Box } from '@mui/system';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from "@tanstack/react-query";

import useEventDetailsQuery from "@components/common/hooks/api/useEventDetailsQuery";
import DataLoadingError from "@components/common/DataLoadingError";
import { API_URLS, axiosRequest } from "@lib/axios";
import { PageContext } from '@lib/contexts/pageContext';
import moment from '@lib/moment';
import { AbilityContext, Subjects } from '@lib/permissions';
import { scrollToAlert, sortDisplayOrder } from '@lib/utils';
import SessionForm from '../SessionForm';

const CreateSession = () => {
  const {
    setBreadcrumbValue,
    setPageMessage,
    setPageErrorMessage,
  } = useContext(PageContext);

  const [submitting, setSubmitting] = useState(false);
  const valuesSetState = useState(null);

  const ability = useAbility(AbilityContext);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { eventID } = useParams();

  const form = useForm({
    mode: 'onSubmit',
    defaultValues: {
      event_id: null,
      session_name: null,
      lesson: null,
      lesson_id: null,
      curriculum_id: null,
      start: null,
      end: null,
      delivery_method: null,
      delivery_url: null,
      delivery_language: null,
      material_language: null,
      delivery_location: null,
      other_details: null,
      demonstration: null,
      recipes: null,
      code: null,
      updated_by: null,
    }
  });

  const { data: event, isLoading, isError, error } = useEventDetailsQuery(eventID);

  // console.log('Event details', event, isError, error);

  useEffect(() => {
    if (event) {
      setBreadcrumbValue(':event', event);
      sortDisplayOrder(event?.curriculum?.lessons, 'lesson_name');
    }
    // eslint-disable-next-line
  }, [event]);

  const createMutation = useMutation({
    mutationFn: async (input) => axiosRequest({
      method: 'POST',
      url: `${API_URLS.SESSIONS}`,
      data: input,
    }),
    onSuccess: async (session) => {
      await queryClient.invalidateQueries({ queryKey: ['events'] });
      await queryClient.invalidateQueries({ queryKey: ['sessions'] });
      navigate(`/events/${event.id}/${session.id}`, { state: { message: 'Session created.' }});
    },
    onError: async (err) => {
      setPageErrorMessage('Error creating session. Please try again.', err);
      console.log('[CreateSession] error:', err);
      scrollToAlert();
    },
    onSettled: () => {
      setSubmitting(false);
    },
  });

  const onSubmit = async (values) => {
    setPageMessage(null);
    setPageErrorMessage(null);
    setSubmitting(true);

    // // Generate a unique short code for the event check-in process
    // const shortCode = await generateUniqueShortCode();
    // // Generate a QR code from the event short code
    // const qrCode = await generateQRCode(shortCode);

    const input = Object.keys(values).reduce((acc, key) => {
      const value = values[key];
      switch (key) {
        case 'lesson':
          if (value?.id) {
            acc[`${key}_id`] = value?.id;
          }
          if (event.event_type === 'BOOTH') {
            acc.session_name = value?.label;
          }
          break;
        case 'start':
        case 'end':
          acc[key] = moment(value).toISOString(true);
          break;
        case 'session_name':
          acc[key] = value?.trim();
          break;
        case 'delivery_method':
        case 'delivery_url':
        case 'delivery_location':
        case 'other_details':
        case 'recipes':
          acc[key] = value?.id || value;
          break;
        case 'delivery_language':
        case 'material_language':
          acc[key] = value;
          break;
        case 'collaborators':
          acc[key] = value ? value.map(v => v.id) : [];
          break;
        case 'demonstration':
          acc[key] = value === 'true';
          break;
        default: break;
      }
      return acc;
    }, {
      event_id: eventID,
      status: 'INCOMPLETE',
    });

    createMutation.mutate(input);
  };

  if (isLoading) {
    return (
      <Box width="100%" textAlign="center">
        <CircularProgress />
      </Box>
    );
  }

  if (isError && error.status !== 404) {
    return <DataLoadingError title={'Failed to load event!'} errors={error.errors} />;
  }

  if (!event) {
    return <>You do not have access, or the event was not found.</>;
  }

  // Staff users can only create events on sessions they own
  if (ability.cannot('create', subject(Subjects.SESSION, { event }))) {
    return <>You do not have permission to create sessions for events owned by other staff members.</>;
  }

  return (
    <Grid container gap={3} wrap={'nowrap'} columns={10}>
      <Grid xs={10}>
        <SessionForm
          form={form}
          event={event}
          session={false}
          submitting={submitting}
          onSubmit={onSubmit}
          valuesSetState={valuesSetState}
        />
      </Grid>
    </Grid>
  );
};

export default CreateSession;
